<template>
  <bid-open-center
    :project-id="projectId"
    :project-info="projectInfo"
    :show-footer="showFooter"
    :bid-file-type="bidFileType"
    :theme="theme"
  >
    <template v-slot:body>
      <div class="mark-step" :class="{ light: theme === 'light' }">
        <div class="operation">
          <div class="flex">
            <div class="list-total">
              共
              <span>{{ total }}</span>
              条数据
            </div>
            <div v-if="controlPriceConfig.controlPrice" class="control-price">
              <span class="m-r-8">
                控制价: {{ controlPriceConfig.controlPrice }} {{ offerUnit }}
              </span>
              <template v-if="offerUnit === '元'">
                <span class="price-cn-prefix">人民币</span>
                <money-in-cn
                  :money="controlPriceConfig.controlPrice"
                  :show-prefix="false"
                />
              </template>
            </div>
          </div>
          <el-button type="primary" @click="printPdf">下载开标一览表</el-button>
        </div>

        <el-table
          :data="list"
          class="m-t-24 hall-table"
          :highlight-current-row="false"
          :row-style="{ fontSize: '22px' }"
          :header-cell-style="{ fontSize: '22px' }"
        >
          <el-table-column
            v-for="item in markList"
            :key="item.markId"
            :label="
              item.markUnit
                ? `${item.markName}(${item.markUnit})`
                : item.markName
            "
            :align="columnAlign(item)"
            header-align="center"
          >
            <template slot-scope="{ row }">
              <div
                class="row-content"
                :class="{ highlight: showHighlight(item.markType, row) }"
              >
                {{ columnContent(item, row) }}
              </div>
              <money-in-cn
                v-if="isMoney(item)"
                :money="columnMoneyContent(item.markId, row)"
                :show-prefix="false"
                class="m-t-10"
                :style="{
                  color: theme === 'dark' ? '#27d8fe' : '',
                  fontSize: '22px'
                }"
                :multiple="getMoneyMultiple(item)"
              />
            </template>
          </el-table-column>
          <el-table-column label="开标现场备注" align="center">
            <template slot-scope="{ row, $index }">
              <div v-if="row.inEdit" class="notes-edit">
                <el-input
                  v-model="copyList[$index].openOfferBidRemark"
                  type="textarea"
                  :rows="1"
                  maxlength="50"
                  class="m-r-12"
                />
                <el-button type="text" @click="cancel(row, $index)">
                  取消
                </el-button>
                <el-button type="text" @click="confirm(row, $index)">
                  完成
                </el-button>
              </div>
              <div v-else>
                <span class="m-r-12">{{ row.openOfferBidRemark || '-' }}</span>
                <i
                  v-if="canEditRemark"
                  class="el-icon-edit"
                  style="cursor: pointer"
                  @click="edit(row)"
                ></i>
              </div>
            </template>
          </el-table-column>
        </el-table>

        <pagination
          v-if="total > 0"
          :total="total"
          :page.sync="page"
          :limit.sync="size"
          :page-sizes="[5, 10]"
          @pagination="handlePagination"
        />
      </div>
    </template>
    <template v-slot:foot>
      <div class="in-mark flex row-between">
        <el-checkbox v-model="publishToSupplier">
          公开开标一览表到供应商
        </el-checkbox>
        <el-button type="primary" @click="endBidOpen">开标结束</el-button>
      </div>
    </template>
  </bid-open-center>
</template>

<script>
import MoneyInCn from '@/components/common/MoneyInCn.vue'
import Pagination from '@/components/common/Pagination.vue'
import BidOpenCenter from '@/components/kpb/open/BidOpenCenter.vue'
import {
  bidFileTypeDict,
  bidMarkTypeDict,
  bidMarkUnitTypeDict,
  isOpenMarkDict
} from '@/dictionary/project/kpb-project'
import {
  controlMethodDict,
  quoteOpenStatusDict
} from '@/dictionary/hall/open-bid-hall'
import {
  addQuoteOpenRemarkApi,
  finishQuoteOpenApi,
  getQuoteOpenMarkPageApi
} from '@/api/hall/quote-open'
import { strToThousands } from '@/utils/utils'
import useOpenMark from '@/components/kpb/hooks/useOpenMark'

/**
 * 报价开标一览表
 */
export default {
  name: 'QuoteOpenMark',

  components: {
    Pagination,
    MoneyInCn,
    BidOpenCenter
  },

  props: {
    projectId: {
      type: [String, Number],
      default: 0
    },

    project: {
      type: Object,
      default: () => {}
    },

    theme: {
      validator: function(value) {
        return ['dark', 'light'].indexOf(value) !== -1
      },
      default: 'dark'
    },

    // 控制价信息
    controlPriceConfig: {
      type: Object,
      default: () => {}
    },

    // 报价单位
    offerUnit: {
      type: String,
      default: ''
    }
  },

  data() {
    return {
      // 唱标列表
      list: [],
      page: 1,
      size: 5,
      total: 10,
      // 唱标列表备份数组，只保留主键id和现场备注信息，用于临时编辑的数据操作
      copyList: [],

      // 是否公开到开标一览表
      publishToSupplier: false
    }
  },

  computed: {
    projectInfo() {
      return this.project?.projectInfo || {}
    },

    markList() {
      return (this.project.markList || []).filter(
        item => item.bidFileType === bidFileTypeDict.QUOTATION.value
      )
    },

    bidFileType() {
      return bidFileTypeDict.QUOTATION.value
    },

    // 允许编辑开标现场备注
    canEditRemark() {
      // 仅开标结束以及之后的状态不能编辑现场备注
      return this.project?.offerBidOpenStatus < quoteOpenStatusDict.END.value
    },

    showFooter() {
      // 唱标时显示
      return (
        Number(this.project.offerBidOpenStatus) ===
        quoteOpenStatusDict.OPEN_MARK.value
      )
    }
  },

  created() {
    this.getMarkList()
  },

  methods: {
    getMarkList() {
      getQuoteOpenMarkPageApi(this.projectId, {
        page: this.page,
        size: this.size
      })
        .then(res => {
          const { data = {} } = res
          this.list = (data.records || []).filter(item => item.itemList)
          this.total = data.total || 0

          this.copyList = this.list.map(item => {
            return {
              bidderId: item.bidderId,
              openOfferBidRemark: item.openOfferBidRemark
            }
          })
        })
        .catch(err => {
          if (err?.message) this.$message.error(err.message)
        })
    },

    /**
     * 进入编辑状态
     */
    edit(row) {
      this.$set(row, 'inEdit', true)
    },

    /**
     * 取消添加或修改现场备注
     */
    cancel(row, index) {
      this.$set(row, 'inEdit', false)
      this.copyList[index].openOfferBidRemark = ''
    },

    /**
     * 确认添加或修改现场备注
     */
    confirm(row, index) {
      const remark = this.copyList[index].openOfferBidRemark
      addQuoteOpenRemarkApi(this.projectId, row.bidderId, { remark })
        .then(res => {
          this.getMarkList()
          row.inEdit = false
        })
        .catch(err => {
          if (err?.message) this.$message.error(err.message)
        })
    },

    columnContent(markItem, row) {
      const markId = markItem.markId
      const isSupplierNameMark =
        Number(markItem.markType) === bidMarkTypeDict.BID_PERSON.value
      if (isSupplierNameMark) {
        return row.bidderName
      }

      const index = row.itemList.findIndex(item => item.markId === markId)
      const item = row.itemList[index]
      if (item.markUnit === '元') {
        return item.markValue ? strToThousands(item.markValue) : '-'
      }
      return item.markValue || '-'
    },

    // 当前内容是否需要高亮
    showHighlight(markType, row) {
      if (
        markType === bidMarkTypeDict.BID_TOTAL.value ||
        markType === bidMarkTypeDict.BID_PERSON.value
      ) {
        if (
          this.controlPriceConfig.controlMethod ===
          controlMethodDict.HIGHER.value
        ) {
          return (
            Number(row.offerAmount) >
            Number(this.controlPriceConfig.controlPrice)
          )
        }
        if (
          this.controlPriceConfig.controlMethod ===
          controlMethodDict.LOWER.value
        ) {
          return (
            Number(row.offerAmount) <
            Number(this.controlPriceConfig.controlPrice)
          )
        }
      }
      return false
    },

    columnMoneyContent(markId, row) {
      const index = row.itemList.findIndex(item => item.markId === markId)
      const item = row.itemList[index]
      let content = item.markValue || '-'
      if (
        item.markUnit === bidMarkUnitTypeDict.YUAN.value ||
        item.markUnit === bidMarkUnitTypeDict.TEN_THOUSAND.value
      ) {
        content = strToThousands(content.toString())
      }
      return content
    },

    /**
     * 获取金额的倍率
     */
    getMoneyMultiple(item) {
      if (item.markUnit === bidMarkUnitTypeDict.TEN_THOUSAND.value) {
        return 10000
      }
      return 1
    },

    handlePagination() {
      this.getMarkList()
    },

    /**
     * 打印开标一览表
     */
    async printPdf() {
      try {
        const openMarkService = useOpenMark()
        const { list = [] } = await openMarkService.getQuoteOpenMark(
          this.projectId
        )
        await openMarkService.getOpenMarkPdf(
          list,
          this.project,
          bidFileTypeDict.QUOTATION.value
        )
      } catch (err) {
        if (err?.message) this.$message.error(err.message)
      }
    },

    /**
     * 当前列内容文字对齐方式
     */
    columnAlign(item) {
      // 唱标项单位为元或万元时，右对齐，其他居中对齐
      return item.markUnit === bidMarkUnitTypeDict.YUAN.value ||
        item.markUnit === bidMarkUnitTypeDict.TEN_THOUSAND.value
        ? 'right'
        : 'center'
    },

    /**
     * 当前内容为金额类型
     */
    isMoney(item) {
      return (
        item.markUnit === bidMarkUnitTypeDict.YUAN.value ||
        item.markUnit === bidMarkUnitTypeDict.TEN_THOUSAND.value
      )
    },

    /**
     * 结束开标
     */
    endBidOpen() {
      this.$antConfirm({
        title: '确定结束开标吗？',
        okText: '确定',
        cancelText: '取消',
        icon: 'exclamation-circle',
        centered: true,
        onOk: () => {
          // isOpenMark传参类型修改
          const isOpenMark = this.publishToSupplier
            ? isOpenMarkDict.YES.value
            : isOpenMarkDict.NO.value
          finishQuoteOpenApi(this.projectId, { isOpenMark: isOpenMark })
            .then(res => {
              this.$emit('nextStep')
            })
            .catch(err => {
              if (err?.message) this.$message.error(err.message)
            })
        }
      })
    }
  }
}
</script>

<style scoped lang="scss">
.mark-step {
  .control-price {
    font-size: 22px;
    color: $white-color;
    display: flex;
    align-items: center;
    margin-left: 48px;

    .price-cn-prefix {
      color: $fourth-text-color;
      font-size: 16px;
      margin-right: 8px;
    }

    ::v-deep .money-in-cn {
      font-size: 22px;
      color: $white-color;
    }
  }

  // 浅色主题样式修改
  &.light {
    .operation {
      .list-total {
        color: $text-color;
        span {
          color: $primary-color;
        }
      }
    }

    .el-icon-edit {
      color: $primary-color;
    }

    .control-price {
      color: $text-color;

      .price-cn-prefix {
        color: $third-text-color;
      }

      ::v-deep .money-in-cn {
        color: $text-color;
      }
    }
  }

  .operation {
    display: flex;
    align-items: center;
    justify-content: space-between;

    .list-total {
      font-size: 22px;
      color: $white-color;

      span {
        color: #27d8fe;
      }
    }
  }

  .notes-edit {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  &:not(.light) {
    ::v-deep .el-button--text {
      color: #27d8fe !important;
    }
  }
}

.hall-table {
  .row-content {
    font-size: 22px;

    &.highlight {
      color: $danger-color;
    }
  }

  ::v-deep &.el-table {
    .cell {
      line-height: 1.5;
    }
  }

  ::v-deep .el-table__empty-text {
    font-size: 20px;
    line-height: 80px;
  }
}

.in-mark {
  ::v-deep .el-checkbox {
    color: $white-color;

    .el-checkbox__input {
      .el-checkbox__inner {
        background-color: transparent;
      }

      &.is-checked {
        .el-checkbox__inner {
          background-color: #27d8fe;
        }
      }
      &.is-checked + .el-checkbox__label {
        color: #27d8fe;
      }
    }
  }
}
</style>
